import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  BarChart3,
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Compass,
  Crown,
  Flame,
  Info,
  Link2,
  MapPin,
  Palette,
  Plus,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  Trophy,
  Zap,
} from "lucide-react";
import {
  api,
  demoCategories,
  demoCreators,
  demoHome,
  demoProfile,
  withDemoFallback,
  type BillingPeriod,
  type Category,
  type CreatorSummary,
  type PremiumPlan,
} from "../lib/api";
import { queryKeys } from "../lib/query";
import { useAuth } from "../lib/auth";
import { classNames, formatCurrency, formatNumber, formatScore } from "../lib/format";
import { getSeoForPath, Seo } from "../lib/seo";
import {
  Avatar,
  Breadcrumbs,
  Button,
  Card,
  CreatorCard,
  EmptyState,
  ErrorState,
  Eyebrow,
  LoadingState,
  ScoreRing,
  SkeletonGrid,
  buttonClass,
} from "../components/ui";
import {
  formAlertClass,
  formFieldClass,
  successMessageClass,
} from "../components/ui-classes";
import { publicStyles } from "../components/public-classes";
import { copyText } from "../components/copy-link";
import { getTrackingIdentity, reportTrackingFailure } from "../lib/tracking";
import {
  PublicProfileView,
  publicProfileTheme,
} from "../components/public-profile-view";
import {
  NETWORK_BY_FIELD,
  NETWORK_FIELD_ORDER,
  NETWORK_LABELS,
} from "../lib/networks";
import { NetworkIcon } from "../components/brand-icons";
import { getCategoryIcon } from "../lib/category-icons";
import {
  brazilianStates,
  stateCodeFromPath,
  stateNameFromPath,
} from "../lib/br-states";
import homeInfluencersUrl from "../assets/home-influencers.webp";
import { ScrollScrub } from "../components/scroll-scrub/scroll-scrub";
import { Reveal, SpotlightSurface } from "../components/public-effects";

function PageSeo({ path }: { path: string }) {
  return <Seo data={getSeoForPath(path)} />;
}

export function HomePage() {
  const navigate = useNavigate();
  const cta = useProfileCta();
  const [search, setSearch] = useState("");
  const homeQuery = useQuery({
    queryKey: queryKeys.home,
    queryFn: () => withDemoFallback(api.home(), demoHome),
  });
  const data = homeQuery.data;

  useEffect(() => {
    const campaignIds = (data?.featured || [])
      .map((creator) => creator.featuredCampaignId)
      .filter((id): id is string =>
        Boolean(id && /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(id)),
      );
    if (!campaignIds.length) return;

    function sendImpressions() {
      if (!getTrackingIdentity()) return;
      campaignIds.forEach((campaignId) => {
        const storageKey = `score-featured-impression:${campaignId}`;
        if (sessionStorage.getItem(storageKey)) return;
        sessionStorage.setItem(storageKey, "pending");
        void api
          .trackFeaturedImpression(campaignId)
          .then(() => sessionStorage.setItem(storageKey, "sent"))
          .catch(() => sessionStorage.removeItem(storageKey));
      });
    }

    sendImpressions();
    window.addEventListener("score-consent-changed", sendImpressions);
    return () =>
      window.removeEventListener("score-consent-changed", sendImpressions);
  }, [data?.featured]);

  function submit(event: FormEvent) {
    event.preventDefault();
    navigate(
      `/buscar${search.trim() ? `?q=${encodeURIComponent(search.trim())}` : ""}`,
    );
  }

  return (
    <>
      <PageSeo path="/" />
      <div className={publicStyles.home}>
        <ScrollScrub
          skipTo={{ label: "Pular para o ranking", targetId: "ranking-home" }}
          theme={{
            accent: "var(--lime)",
            background: "var(--ink)",
            ink: "#ffffff",
            muted: "#d3cbd6",
          }}
          scenes={[
            {
              actions: (
                <>
                  <form
                    className={publicStyles.homeSearch}
                    onSubmit={submit}
                    role="search"
                  >
                    <Search aria-hidden="true" />
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Busque por nome, categoria ou cidade"
                      aria-label="Buscar criadores"
                    />
                    <Button type="submit" variant="primary">
                      Buscar
                    </Button>
                  </form>
                  <div className={publicStyles.homeStats}>
                    <span>
                      <strong>{formatNumber(data?.stats.creators || 0)}</strong>{" "}
                      criadores
                    </span>
                    <i />
                    <span>
                      <strong>{formatNumber(data?.stats.cities || 0)}</strong>{" "}
                      cidades
                    </span>
                    <i />
                    <span>
                      <strong>
                        {formatNumber(data?.stats.monthlyViews || 0)}
                      </strong>{" "}
                      visualizações
                    </span>
                  </div>
                </>
              ),
              align: "left",
              body: "Descubra criadores, acompanhe quem está crescendo e transforme presença digital em novas oportunidades. Um ranking transparente e orgânico, sem atalhos pagos.",
              clip: "/media/home-creators-scroll.mp4",
              id: "intro",
              kicker: (
                <>
                  <Trophy size={14} aria-hidden="true" /> Onde a criação
                  brasileira ganha contexto
                </>
              ),
              label: "Início",
              mobileObjectPosition: "82% 30%",
              objectPosition: "76% 36%",
              poster: homeInfluencersUrl,
              scroll: 3,
              title: (
                <>
                  Talento que merece
                  <br />
                  <em>ser encontrado.</em>
                </>
              ),
            },
          ]}
        />

        <section className={publicStyles.homeRankingSection} id="ranking-home">
          <div className={classNames(publicStyles.shell, publicStyles.homeRanking)}>
          <Reveal className={publicStyles.homeHeading}>
            <div>
              <Eyebrow tone="lime">
                <span className="size-[7px] rounded-full bg-current shadow-[0_0_0_5px_color-mix(in_srgb,currentColor_20%,transparent)]" />{" "}
                Atualizado diariamente
              </Eyebrow>
              <h2>Os dez nomes no ritmo de agora.</h2>
              <p>
                Três posições em evidência e mais sete criadores para acompanhar
                de perto. Atualização orgânica, sem atalhos pagos.
              </p>
            </div>
            <Link to="/ranking" className={buttonClass("secondary")}>
              Ver ranking completo <ArrowRight size={17} />
            </Link>
          </Reveal>
          <Reveal delayMs={80}>
            <HomeRankingSpotlight
              items={data?.ranking}
              loading={homeQuery.isLoading}
              error={homeQuery.isError}
              onRetry={() => void homeQuery.refetch()}
            />
          </Reveal>
          </div>
        </section>
      </div>

      <section className={publicStyles.homeFeaturedSection}>
        <div className={publicStyles.shell}>
          <Reveal className={publicStyles.homeFeaturedHeading}>
            <div>
              <Eyebrow>
                <Sparkles size={14} /> Em destaque
              </Eyebrow>
              <h2>Campanhas que colocam bons trabalhos em circulação.</h2>
              <p>
                Uma vitrine separada do ranking, identificada com clareza. Mais
                alcance para o criador, sem interferir no Creator Score.
              </p>
            </div>
            <Link to="/buscar" className={buttonClass("ghost")}>
              Explorar todos <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delayMs={70}>
            <HomeFeaturedStrip
              items={data?.featured}
              loading={homeQuery.isLoading}
              error={homeQuery.isError}
              onRetry={() => void homeQuery.refetch()}
            />
          </Reveal>
        </div>
      </section>

      <section className={classNames(publicStyles.section, publicStyles.homeScoreSection)}>
        <Reveal className={classNames(publicStyles.shell, publicStyles.scoreStory)}>
          <div className={publicStyles.scoreStoryCopy}>
            <Eyebrow tone="lime">Uma leitura, quatro sinais</Eyebrow>
            <h2>
              Menos achismo.
              <br />
              <em>Mais direção.</em>
            </h2>
            <p>
              O Creator Score traduz quatro dimensões da sua presença em uma
              leitura acionável. Você entende o que já funciona, o que está
              travando seu alcance e onde investir energia agora.
            </p>
            <span className={publicStyles.scoreOrganicNote}>
              <ShieldCheck size={16} /> Tráfego patrocinado fica fora do cálculo
            </span>
            <Link to="/sobre#score" className={buttonClass("primary")}>
              Como o score funciona <ArrowRight size={17} />
            </Link>
          </div>
          <div className={publicStyles.scoreStoryBoard}>
            <div className={publicStyles.scoreStoryRing}>
              <small>Exemplo de leitura</small>
              <ScoreRing value={82} size={190} />
            </div>
            {[
              { icon: CircleCheck, label: "Perfil completo", score: 28, max: 30 },
              { icon: BarChart3, label: "Engajamento", score: 27, max: 35 },
              { icon: Compass, label: "Descoberta", score: 19, max: 25 },
              { icon: Zap, label: "Atividade", score: 8, max: 10 },
            ].map(({ icon: Icon, label, score, max }) => (
              <div className={publicStyles.scoreFactor} key={label}>
                <span>
                  <Icon size={18} />
                </span>
                <div className={publicStyles.scoreFactorHead}>
                  <small>{label}</small>
                  <strong>
                    {score}/{max}
                  </strong>
                </div>
                <div className={publicStyles.scoreFactorTrack}>
                  <span
                    className={publicStyles.scoreFactorFill}
                    style={{ width: `${Math.round((score / max) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <HomeCreatorSection
        className={publicStyles.homeRisingSection}
        variant="rising"
        eyebrow="Criadores em crescimento"
        title={
          <>
            Movimentos que
            <br />
            <em>merecem atenção.</em>
          </>
        }
        description="Perfis cuja presença orgânica mais evoluiu no período recente."
        items={data?.rising}
        loading={homeQuery.isLoading}
        error={homeQuery.isError}
        onRetry={() => void homeQuery.refetch()}
        emptyTitle="Crescimento sendo calculado"
        emptyMessage="Esta seleção aparece assim que houver histórico orgânico comparável."
        link="/ranking/crescimento"
        linkLabel="Ver quem está crescendo"
      />

      <HomeCreatorSection
        className={publicStyles.homeNewestSection}
        variant="newest"
        eyebrow="Novos criadores"
        title={
          <>
            Gente nova
            <br />
            <em>chegando ao mapa.</em>
          </>
        }
        description="Os perfis publicados mais recentemente, de diferentes cantos e categorias."
        items={data?.newest}
        loading={homeQuery.isLoading}
        error={homeQuery.isError}
        onRetry={() => void homeQuery.refetch()}
        emptyTitle="Aguardando os primeiros perfis"
        emptyMessage="Novos criadores publicados aparecerão aqui."
        link="/ranking/novos"
        linkLabel="Explorar novidades"
      />

      <section className={classNames(publicStyles.section, publicStyles.homeCategoriesSection)}>
        <Reveal className={publicStyles.shell}>
          <div className={classNames(publicStyles.sectionHeading, publicStyles.sectionHeadingSplit)}>
            <div>
              <Eyebrow>Encontre seu território</Eyebrow>
              <h2>
                Vinte e cinco universos.
                <br />
                <em>Uma busca mais humana.</em>
              </h2>
              <p>
                Comece pelas categorias que já estão movimentando conversas,
                projetos e comunidades.
              </p>
            </div>
            <Link to="/categorias" className={buttonClass("secondary")}>
              Ver todas as categorias <ArrowRight size={17} />
            </Link>
          </div>
          {homeQuery.isLoading ? (
            <div className={publicStyles.categoryGrid}>
              {Array.from({ length: 6 }, (_, i) => (
                <div className={publicStyles.skeletonCategory} key={i} />
              ))}
            </div>
          ) : homeQuery.isError ? (
            <ErrorState onRetry={() => void homeQuery.refetch()} />
          ) : data?.categories.length ? (
            <CategoryGrid categories={data.categories.slice(0, 9)} />
          ) : (
            <EmptyState
              title="Categorias a caminho"
              message="As categorias aparecerão aqui assim que os primeiros perfis forem publicados."
            />
          )}
        </Reveal>
      </section>

      <section
        className={classNames(publicStyles.section, publicStyles.homePremiumSection)}
      >
        <Reveal className={classNames(publicStyles.shell, publicStyles.homePremiumLayout)}>
          <div
            className={classNames(
              publicStyles.sectionHeading,
              "mb-0 flex flex-col items-start gap-8",
            )}
          >
            <div>
              <Eyebrow>Premium por 7 dias</Eyebrow>
              <h2>
                Seu perfil gratuito.
                <br />
                <em>Mais inteligência quando quiser.</em>
              </h2>
              <p>
                Publique sem cartão. Quando fizer sentido, teste analytics,
                links ilimitados e personalização Premium pelo painel.
              </p>
            </div>
            <Link to="/precos" className={buttonClass("dark")}>
              Conhecer os planos <ArrowRight size={17} />
            </Link>
          </div>
          <div className={publicStyles.homeBenefitGrid}>
            {[
              {
                icon: Link2,
                title: "Links ilimitados",
                text: "Organize seus melhores caminhos em uma página pública.",
              },
              {
                icon: BarChart3,
                title: "Analytics detalhado",
                text: "Entenda visitas, cliques, origens e conversão.",
              },
              {
                icon: TrendingUp,
                title: "Histórico do score",
                text: "Acompanhe sua evolução e os sinais que mudaram.",
              },
              {
                icon: Palette,
                title: "Personalização",
                text: "Ajuste a presença visual e exiba o selo Premium.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <Card key={title} className={publicStyles.benefit}>
                <span>
                  <Icon aria-hidden="true" />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </Card>
            ))}
          </div>
        </Reveal>
      </section>

      <section className={classNames(publicStyles.section, publicStyles.homeFaqSection)}>
        <Reveal className={classNames(publicStyles.shell, publicStyles.homeFaqLayout)}>
          <div className={classNames(publicStyles.sectionHeading, "mb-0")}>
            <Eyebrow>Perguntas frequentes</Eyebrow>
            <h2>
              Tudo claro
              <br />
              <em>antes de começar.</em>
            </h2>
            <p>
              Perfil gratuito, score orgânico e recursos Premium sem letras
              miúdas.
            </p>
            <Link to="/faq" className={buttonClass("ghost")}>
              Ver todas as respostas <ArrowRight size={16} />
            </Link>
          </div>
          <div className={publicStyles.homeFaqList}>
            {homeFaq.map((item, index) => (
              <details key={item.title}>
                <summary>
                  <small>{String(index + 1).padStart(2, "0")}</small>
                  <strong>{item.title}</strong>
                  {/* An icon rather than a "+" character: the glyph sits on the
                      font's math axis, above the optical centre, so it reads as
                      misaligned inside the circle no matter how the box is
                      centred. The SVG is centred on its own viewBox. */}
                  <span aria-hidden="true">
                    <Plus />
                  </span>
                </summary>
                <p>{item.text}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </section>

      <section
        className={classNames(publicStyles.section, publicStyles.sectionCoral)}
      >
        <Reveal className={classNames(publicStyles.shell, publicStyles.creatorCta)}>
          <div>
            <Eyebrow tone="lime">Agora é a sua vez</Eyebrow>
            <h2>
              Seu trabalho já existe.
              <br />
              <em>Faça ele circular.</em>
            </h2>
            <p>
              {cta.hasProfile
                ? "Seu perfil já está no ar. Acompanhe seu Creator Score e mantenha seus links em dia."
                : "Seu perfil público gratuito para começar a ser encontrado. Recursos Premium podem ser ativados depois."}
            </p>
            <div className={publicStyles.ctaActions}>
              <Link to={cta.freeHref} className={buttonClass("dark", "lg")}>
                {!cta.signedIn
                  ? "Criar meu perfil grátis"
                  : cta.hasProfile
                    ? "Ir para meu painel"
                    : "Concluir meu perfil"}{" "}
                <ArrowRight size={18} />
              </Link>
              <span>
                <CircleCheck size={16} />{" "}
                {cta.hasProfile ? "Publicado e gratuito" : "Sem cartão para publicar"}
              </span>
            </div>
          </div>
          <div className={publicStyles.ctaSteps}>
            {[
              "Monte seu perfil",
              "Publique gratuitamente",
              "Compartilhe e acompanhe seu score",
            ].map((step, index) => (
              <div key={step}>
                <span>{index + 1}</span>
                <strong>{step}</strong>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
}

const homeFaq = [
  {
    title: "Preciso pagar para manter meu perfil público?",
    text: "Não. A criação e a publicação do perfil são gratuitas e não exigem cartão.",
  },
  {
    title: "Quando a assinatura Premium começa a valer?",
    text: "Assim que a Stripe confirmar o pagamento. Isso não interfere na publicação gratuita do perfil.",
  },
  {
    title: "O destaque altera meu Creator Score?",
    text: "Não. Tráfego patrocinado aparece no Analytics, mas é excluído do score e dos rankings orgânicos.",
  },
  {
    title: "Como ganho o bônus de indicação?",
    text: "Quando dez contas novas atribuídas ao seu link confirmam o e-mail, você recebe uma única recompensa de 30 dias Premium.",
  },
];

function HomeCreatorSection({
  className = "",
  eyebrow,
  title,
  description,
  items,
  loading,
  error,
  onRetry,
  emptyTitle,
  emptyMessage,
  link,
  linkLabel,
  variant,
}: {
  className?: string;
  eyebrow: string;
  title: ReactNode;
  description: string;
  items?: CreatorSummary[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  emptyTitle: string;
  emptyMessage: string;
  link: string;
  linkLabel: string;
  variant: "rising" | "newest";
}) {
  return (
    <section className={classNames(publicStyles.section, className)}>
      <Reveal className={publicStyles.shell}>
        <div
          className={classNames(publicStyles.sectionHeading, publicStyles.sectionHeadingSplit)}
        >
          <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
          <Link to={link} className={buttonClass("secondary")}>
            {linkLabel} <ArrowRight size={17} />
          </Link>
        </div>
        {loading ? (
          <SkeletonGrid />
        ) : error ? (
          <ErrorState onRetry={onRetry} />
        ) : items?.length ? (
          <div
            className={
              variant === "rising"
                ? publicStyles.homeGrowthGrid
                : publicStyles.homeNewGrid
            }
          >
            {items.slice(0, 6).map((creator, index) => (
              <Link
                key={creator.id}
                to={`/criador/${creator.slug}`}
                className={
                  variant === "rising"
                    ? publicStyles.homeGrowthCard
                    : publicStyles.homeNewCard
                }
              >
                <span
                  className={classNames(
                    publicStyles.homeCreatorIndex,
                    variant === "newest" && "hidden",
                  )}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <Avatar name={creator.name} src={creator.avatarUrl} size="lg" />
                <div
                  className={classNames(
                    publicStyles.homeCreatorIdentity,
                    variant === "newest" && publicStyles.homeNewIdentity,
                  )}
                >
                  <small>
                    {variant === "rising" ? "Em movimento" : "Novo no mapa"}
                  </small>
                  <h3>
                    {creator.name}
                    {creator.isVerified && (
                      <BadgeCheck aria-label="Perfil verificado" />
                    )}
                  </h3>
                  <p>{creator.headline}</p>
                  <span>
                    {creator.category} · {creator.city}, {creator.state}
                  </span>
                </div>
                {variant === "rising" ? (
                  <div className={publicStyles.homeCreatorScoreStack}>
                    <strong className={publicStyles.homeCreatorScore}>
                      {formatScore(creator.score)}
                      <small>score</small>
                    </strong>
                    {creator.scoreChange && creator.scoreChange > 0 ? (
                      <span className={publicStyles.homeGrowthBadge}>
                        <TrendingUp size={11} aria-hidden="true" /> +
                        {creator.scoreChange}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <strong className={classNames(publicStyles.homeCreatorScore, "hidden")}>
                    {formatScore(creator.score)}
                    <small>score</small>
                  </strong>
                )}
                <ArrowUpRight aria-hidden="true" />
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState title={emptyTitle} message={emptyMessage} />
        )}
      </Reveal>
    </section>
  );
}

function HomeRankingSpotlight({
  items,
  loading,
  error,
  onRetry,
}: {
  items?: CreatorSummary[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  if (loading)
    return (
      <div className="grid gap-5">
        <div className={classNames(publicStyles.homePodium, "min-[900px]:grid-cols-[.9fr_1.12fr_.9fr]") }>
          {Array.from({ length: 3 }, (_, index) => (
            <div className={publicStyles.homeSkeleton} key={index} />
          ))}
        </div>
        <div className={publicStyles.homeNext}>
          {Array.from({ length: 7 }, (_, index) => (
            <div className={publicStyles.homeRowSkeleton} key={index} />
          ))}
        </div>
      </div>
    );
  if (error)
    return (
      <ErrorState
        message="Não foi possível carregar o ranking agora."
        onRetry={onRetry}
      />
    );
  if (!items?.length)
    return (
      <EmptyState
        title="Ranking em atualização"
        message="As primeiras posições aparecerão após o próximo cálculo diário."
      />
    );
  const podium = items.slice(0, 3);
  const nextSeven = items.slice(3, 10);
  const topScore = items[0]?.score || 1;
  return (
    <div className="grid gap-7">
      <div
        className={classNames(
          publicStyles.homePodium,
          podium.length === 1 &&
            "min-[900px]:mx-auto min-[900px]:max-w-[520px] min-[900px]:grid-cols-1",
          podium.length === 2 &&
            "min-[900px]:grid-cols-2",
          podium.length >= 3 &&
            "min-[900px]:grid-cols-[.92fr_1.16fr_.92fr]",
        )}
      >
        {podium.map((creator) => {
          const position =
            creator.position ||
            items.findIndex((item) => item.id === creator.id) + 1;
          const isFirst = position === 1;
          return (
            <SpotlightSurface
              as="article"
              className={classNames(
                publicStyles.homePodiumCard,
                isFirst && publicStyles.homePodiumFirst,
                position === 2 && publicStyles.homePodiumSecond,
                position === 3 && publicStyles.homePodiumThird,
                !isFirst && publicStyles.homePodiumSecondary,
              )}
              key={creator.id}
              data-ranking-position={position}
              spotlightColor={
                isFirst
                  ? "rgba(216, 183, 242, 0.24)"
                  : "rgba(216, 183, 242, 0.11)"
              }
            >
              <div
                className={classNames(
                  publicStyles.homePodiumRank,
                  isFirst && publicStyles.homePodiumRankFirst,
                )}
              >
                <span>#{position}</span>
                {isFirst && <Crown aria-label="Primeiro lugar" />}
              </div>
              {isFirst ? (
                <div className={publicStyles.homePodiumAvatarWrap}>
                  <Avatar name={creator.name} src={creator.avatarUrl} size="xl" />
                </div>
              ) : (
                <Avatar name={creator.name} src={creator.avatarUrl} size="xl" />
              )}
              <div
                className={classNames(
                  publicStyles.homePodiumScore,
                  isFirst && publicStyles.homePodiumScoreFirst,
                  !isFirst && publicStyles.homePodiumScoreSecondary,
                )}
              >
                <strong>{formatScore(creator.score)}</strong>
                <span>Creator Score</span>
              </div>
              <div className={publicStyles.homePodiumIdentity}>
                <h3>
                  {creator.name}
                  {creator.isVerified && (
                    <BadgeCheck aria-label="Perfil verificado" />
                  )}
                </h3>
                <p>{creator.category}</p>
                <span>
                  <MapPin size={13} /> {creator.city}, {creator.state}
                </span>
              </div>
              <div className={publicStyles.homePodiumFooter}>
                <span className={creator.scoreChange && creator.scoreChange > 0 ? "is-rising" : ""}>
                  {creator.scoreChange && creator.scoreChange > 0 ? (
                    <><TrendingUp size={14} /> +{creator.scoreChange} posições</>
                  ) : (
                    <>Ranking orgânico</>
                  )}
                </span>
                <Link
                  to={`/criador/${creator.slug}`}
                  className={buttonClass(isFirst ? "primary" : "secondary", "sm")}
                >
                  Ver perfil
                </Link>
              </div>
            </SpotlightSurface>
          );
        })}
      </div>
      {nextSeven.length > 0 && (
        <div
          className={publicStyles.homeNext}
          aria-label="Próximas posições do ranking"
        >
          {nextSeven.map((creator, index) => {
            const position = creator.position || index + 4;
            const barWidth = Math.max(
              8,
              Math.min(100, Math.round((creator.score / topScore) * 100)),
            );
            return (
              <Link
                to={`/criador/${creator.slug}`}
                className={publicStyles.homeRow}
                key={creator.id}
                data-ranking-position={position}
              >
                <strong className={publicStyles.homeRowPosition}>
                  {String(position).padStart(2, "0")}
                </strong>
                <Avatar name={creator.name} src={creator.avatarUrl} size="sm" />
                <div className={publicStyles.homeRowIdentity}>
                  <strong>
                    {creator.name}
                    {creator.isVerified && (
                      <BadgeCheck aria-label="Perfil verificado" />
                    )}
                  </strong>
                  <span>
                    {creator.category} · {creator.city}, {creator.state}
                  </span>
                </div>
                <span className={publicStyles.homeRowMovement}>
                  {creator.scoreChange && creator.scoreChange > 0 ? (
                    <><TrendingUp size={14} /> +{creator.scoreChange}</>
                  ) : (
                    "orgânico"
                  )}
                </span>
                <strong className={publicStyles.homeRowScore}>
                  {formatScore(creator.score)}
                  <small> score</small>
                </strong>
                <ArrowRight aria-hidden="true" />
                <span className={publicStyles.homeRowBar} aria-hidden="true">
                  <span
                    className={publicStyles.homeRowBarFill}
                    style={{ width: `${barWidth}%` }}
                  />
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/*
 * Where a "create my profile" call to action should actually send whoever is
 * asking. Written once because the destination was hardcoded to /cadastro in
 * several places at once, and a signed-in creator clicking any of them landed
 * back in the onboarding form with their own profile already filled in.
 *
 * A signed-in account without a profile still goes to the onboarding: checkout
 * and the featured purchase both require a complete profile on the API side.
 */
function useProfileCta() {
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const entitlementsQuery = useQuery({
    queryKey: queryKeys.entitlements,
    queryFn: api.entitlements,
    enabled: signedIn,
    retry: false,
  });
  const profileQuery = useQuery({
    queryKey: queryKeys.profile,
    queryFn: api.myProfile,
    enabled: signedIn,
    retry: false,
  });
  const isPremium = entitlementsQuery.data?.plan === "PREMIUM";
  const hasProfile = Boolean(profileQuery.data);
  return {
    signedIn,
    hasProfile,
    isPremium,
    freeHref: !signedIn ? "/cadastro" : hasProfile ? "/dashboard/perfil" : "/onboarding",
    premiumHref: !signedIn
      ? "/cadastro?plano=premium"
      : hasProfile || isPremium
        ? "/dashboard/assinatura"
        : "/onboarding?plano=premium",
    featuredHref: !signedIn ? "/cadastro" : hasProfile ? "/dashboard/destaques" : "/onboarding",
  };
}

function HomeFeaturedStrip({
  items,
  loading,
  error,
  onRetry,
}: {
  items?: CreatorSummary[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  if (loading)
    return (
      <div className={publicStyles.homeFeaturedStrip}>
        {Array.from({ length: 3 }, (_, index) => (
          <div className={publicStyles.homeFeaturedSkeleton} key={index} />
        ))}
      </div>
    );
  if (error)
    return (
      <ErrorState
        message="Não foi possível carregar os destaques agora."
        onRetry={onRetry}
      />
    );
  if (!items?.length)
    return (
      <div className={publicStyles.homeFeaturedEmpty}>
        <span className={publicStyles.homeFeaturedEmptyIcon}>
          <Sparkles aria-hidden="true" />
        </span>
        <strong>Nenhuma campanha ativa agora</strong>
        <p>
          Os destaques patrocinados aparecerão aqui quando houver campanhas
          em andamento.
        </p>
      </div>
    );
  // Three or fewer campaigns stay put; the carousel only earns its controls
  // once there is something past the fold to scroll to.
  if (items.length > 3) return <FeaturedCarousel items={items} />;
  return (
    <div
      className={classNames(
        publicStyles.homeFeaturedStrip,
        items.length === 1 && publicStyles.homeFeaturedStripOne,
        items.length === 2 && publicStyles.homeFeaturedStripTwo,
      )}
    >
      {items.map((creator, index) => (
        <FeaturedCreatorCard key={creator.id} creator={creator} index={index} />
      ))}
    </div>
  );
}

/*
 * Ten tones so a run of featured cards never repeats before the eleventh.
 *
 * The tone fills the card and every element on top of it is white; the handle
 * pill inverts to white with the tone as its label. One --tone variable drives
 * the fill, the darker end of its gradient and the pill, so a card can never
 * end up with mismatched halves. All ten clear 4.5:1 against white.
 */
const FEATURED_TONES = [
  "#660099",
  "#14548a",
  "#1b6b47",
  "#8a5a12",
  "#a8214f",
  "#0f6b73",
  "#a8431f",
  "#3f37a8",
  "#55650f",
  "#7d2340",
];

function FeaturedCreatorCard({ creator, index, clone = false }: { creator: CreatorSummary; index: number; clone?: boolean }) {
  const tone = FEATURED_TONES[index % FEATURED_TONES.length]!;
  return (
    <SpotlightSurface
      as="article"
      className={publicStyles.homeFeaturedCard}
      style={{ "--tone": tone } as CSSProperties}
      spotlightColor="rgba(255, 255, 255, 0.16)"
      aria-hidden={clone || undefined}
    >
      <span className={publicStyles.homeFeaturedBadge}>
        <Sparkles aria-hidden="true" /> Destaque patrocinado
      </span>
      <Avatar name={creator.name} src={creator.avatarUrl} size="xl" />
      <h3 className={publicStyles.homeFeaturedName}>
        {creator.name}
        {creator.isVerified && <BadgeCheck aria-label="Perfil verificado" />}
      </h3>
      <p className={publicStyles.homeFeaturedRole}>
        {creator.category} · {creator.city}, {creator.state}
      </p>
      {creator.networks && creator.networks.length > 0 && (
        <div className={publicStyles.homeFeaturedSocials}>
          {creator.networks.slice(0, 5).map((network) => (
            <span key={network} title={NETWORK_LABELS[network]}>
              <NetworkIcon network={network} size={15} />
            </span>
          ))}
        </div>
      )}
      <span className={publicStyles.homeFeaturedScore}>
        <strong>{formatScore(creator.score)}</strong>
        <small>score</small>
      </span>
      <Link
        to={`/criador/${creator.slug}`}
        className={publicStyles.homeFeaturedHandle}
        tabIndex={clone ? -1 : undefined}
      >
        <Sparkles aria-hidden="true" /> /{creator.slug}
      </Link>
    </SpotlightSurface>
  );
}

/** Seconds each card takes to cross the row. Speed stays constant however many
 *  campaigns are running, because the duration scales with the count. */
const FEATURED_SECONDS_PER_CARD = 7;

/*
 * Continuous carousel. The row glides at a constant speed instead of jumping a
 * card at a time — stepping every few seconds is what reads as stuttering.
 *
 * The motion is a CSS animation on the track, so it runs on the compositor and
 * stays smooth regardless of what JavaScript is doing. The list is rendered
 * twice and the track slides exactly one copy before the animation restarts;
 * the frame it restarts on is identical to the frame it ended on, so there is
 * no seam to correct and nothing ever resets visibly.
 *
 * The duplicated half is hidden from assistive technology and taken out of the
 * tab order — it is the same content, and announcing every creator twice would
 * be worse than not looping at all.
 */
function FeaturedCarousel({ items }: { items: CreatorSummary[] }) {
  const [motionAllowed, setMotionAllowed] = useState(false);

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return;
    const apply = () => setMotionAllowed(!query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  // Someone who asked for less movement gets a row they move themselves, not a
  // row frozen with most of it clipped out of view.
  if (!motionAllowed) {
    return (
      <div className={publicStyles.homeFeaturedStatic}>
        {items.map((creator, index) => (
          <FeaturedCreatorCard key={creator.id} creator={creator} index={index} />
        ))}
      </div>
    );
  }

  const loop = [...items, ...items];
  return (
    <div className={publicStyles.homeFeaturedViewport}>
      <div
        className={publicStyles.homeFeaturedMarquee}
        style={{ animationDuration: `${items.length * FEATURED_SECONDS_PER_CARD}s` }}
      >
        {loop.map((creator, index) => (
          <FeaturedCreatorCard
            key={`${creator.id}-${index < items.length ? "a" : "b"}`}
            creator={creator}
            // The clone keeps its original's tone so the colour does not shift
            // when the loop comes around.
            index={index % items.length}
            clone={index >= items.length}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryGrid({
  categories,
  collapseAfterMobile,
  expanded = false,
  id,
}: {
  categories: Category[];
  collapseAfterMobile?: number;
  expanded?: boolean;
  id?: string;
}) {
  return (
    <div className={publicStyles.categoryGrid} id={id}>
      {categories.map((category, index) => {
        const Icon = getCategoryIcon(category.slug);
        return (
          <Link
            key={category.id}
            to={`/categoria/${category.slug}`}
            className={classNames(
              "group",
              publicStyles.categoryCard,
              collapseAfterMobile !== undefined &&
                index >= collapseAfterMobile &&
                !expanded &&
                "hidden min-[700px]:flex",
            )}
          >
            <span className={publicStyles.categoryIcon}>
              <Icon aria-hidden="true" />
            </span>
            <div>
              <h3>{category.name}</h3>
              <p>
                {category.creatorCount === 0
                  ? "Em breve"
                  : `${formatNumber(category.creatorCount)} ${category.creatorCount === 1 ? "criador" : "criadores"}`}
              </p>
            </div>
            <ArrowUpRight />
          </Link>
        );
      })}
    </div>
  );
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filters = useMemo(
    () => ({
      query: params.get("q") || "",
      category: params.get("categoria") || "",
      city: params.get("cidade") || "",
      state: params.get("estado") || "",
      tag: params.get("tag") || "",
      network: (params.get("rede") || undefined) as
        "instagram" | "youtube" | undefined,
      minScore: params.get("score") ? Number(params.get("score")) : undefined,
      premium: params.get("premium") === "true" ? true : undefined,
      sort: params.get("ordem") || "relevancia",
      page: Number(params.get("pagina") || 1),
    }),
    [params],
  );
  const [draftQuery, setDraftQuery] = useState(filters.query);
  const [searchEventId, setSearchEventId] = useState<string | null>(null);
  const resultQuery = useQuery({
    queryKey: queryKeys.search(filters),
    queryFn: () =>
      withDemoFallback(api.search(filters), {
        items: demoCreators.filter(
          (creator) =>
            !filters.query ||
            `${creator.name} ${creator.headline} ${creator.category} ${creator.city}`
              .toLowerCase()
              .includes(filters.query.toLowerCase()),
        ),
        meta: {
          page: 1,
          pageSize: 12,
          total: demoCreators.length,
          totalPages: 1,
        },
      }),
  });
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => withDemoFallback(api.categories(), demoCategories),
  });
  const tagsQuery = useQuery({
    queryKey: ["public", "tags"],
    queryFn: api.tags,
  });

  useEffect(() => {
    let active = true;
    function trackSearch() {
      const identity = getTrackingIdentity();
      const trackingToken = resultQuery.data?.meta.trackingToken;
      if (!identity || !trackingToken) return;
      void api
        .trackSearch(trackingToken, identity)
        .then((result) => {
          if (active)
            setSearchEventId(result.id || result.searchEventId || null);
        })
        .catch(reportTrackingFailure);
    }
    trackSearch();
    window.addEventListener("score-consent-changed", trackSearch);
    return () => {
      active = false;
      window.removeEventListener("score-consent-changed", trackSearch);
    };
  }, [filters, resultQuery.data]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("pagina");
    setParams(next);
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    updateParam("q", draftQuery.trim());
  }
  const count = resultQuery.data?.meta.total ?? 0;
  const activeFilterCount = [
    filters.category,
    filters.city,
    filters.state,
    filters.tag,
    filters.network,
    filters.minScore,
    filters.premium,
  ].filter(Boolean).length;

  return (
    <div className={publicStyles.page}>
      <PageSeo path="/buscar" />
      <section
        className={classNames(publicStyles.pageHero, publicStyles.pageHeroCompact)}
      >
        <div className={publicStyles.shell}>
          <Breadcrumbs
            items={[{ label: "Início", to: "/" }, { label: "Explorar" }]}
          />
          <Eyebrow>Seu próximo encontro criativo</Eyebrow>
          <h1>
            Encontre quem faz
            <br />
            <em>do seu jeito.</em>
          </h1>
          <form className={publicStyles.searchBar} onSubmit={submit}>
            <Search />
            <input
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
              placeholder="Busque por nome, especialidade ou cidade"
              aria-label="Termo de busca"
            />
            <Button type="submit">Buscar</Button>
          </form>
          <div className={publicStyles.categoryHeroMetric}>
            <span><Sparkles size={16} /></span>
            <div>
              <strong>Descoberta por relevância</strong>
              <br /> encontre por nome, categoria, cidade ou presença digital
            </div>
          </div>
        </div>
      </section>
      <section className={classNames(publicStyles.section, publicStyles.discoverySection)}>
        <div className={publicStyles.shell}>
          <button
            type="button"
            className={publicStyles.filterToggle}
            onClick={() => setFiltersOpen((current) => !current)}
            aria-expanded={filtersOpen}
            aria-controls="advanced-search-filters"
          >
            <span>
              <SlidersHorizontal size={17} /> Filtros
              {activeFilterCount > 0 && <small>{activeFilterCount}</small>}
            </span>
            <ChevronDown
              size={17}
              className={classNames(
                "transition-transform",
                filtersOpen && "rotate-180",
              )}
            />
          </button>
          <div
            id="advanced-search-filters"
            className={classNames(
              publicStyles.filterBar,
              !filtersOpen && "hidden min-[700px]:grid",
            )}
          >
            <label>
              <span>Categoria</span>
              <select
                value={filters.category}
                onChange={(event) =>
                  updateParam("categoria", event.target.value)
                }
              >
                <option value="">Todas</option>
                {categoriesQuery.data?.map((category) => (
                  <option key={category.id} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Cidade</span>
              <input
                value={filters.city}
                onChange={(event) => updateParam("cidade", event.target.value)}
                placeholder="Todas as cidades"
              />
            </label>
            <label>
              <span>Estado</span>
              <select
                value={filters.state}
                onChange={(event) => updateParam("estado", event.target.value)}
              >
                <option value="">Todos</option>
                {brazilianStates.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Tag</span>
              <select
                value={filters.tag}
                onChange={(event) => updateParam("tag", event.target.value)}
              >
                <option value="">Todas</option>
                {tagsQuery.data?.map((tag) => (
                  <option key={tag.id} value={tag.slug}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Rede</span>
              <select
                value={filters.network || ""}
                onChange={(event) => updateParam("rede", event.target.value)}
              >
                <option value="">Qualquer</option>
                {NETWORK_FIELD_ORDER.map((field) => (
                  <option
                    key={field}
                    value={NETWORK_BY_FIELD[field].toLowerCase()}
                  >
                    {NETWORK_LABELS[NETWORK_BY_FIELD[field]]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Score mínimo</span>
              <select
                value={filters.minScore || ""}
                onChange={(event) => updateParam("score", event.target.value)}
              >
                <option value="">Qualquer</option>
                <option value="50">50+</option>
                <option value="70">70+</option>
                <option value="85">85+</option>
              </select>
            </label>
            <label>
              <span>Plano</span>
              <select
                value={filters.premium ? "true" : ""}
                onChange={(event) => updateParam("premium", event.target.value)}
              >
                <option value="">Todos</option>
                <option value="true">Premium</option>
              </select>
            </label>
            <label>
              <span>Ordenar por</span>
              <select
                value={filters.sort}
                onChange={(event) => updateParam("ordem", event.target.value)}
              >
                <option value="relevancia">Relevância</option>
                <option value="score">Maior score</option>
                <option value="recentes">Mais recentes</option>
                <option value="acessos">Mais acessados</option>
                <option value="compartilhados">Mais compartilhados</option>
                <option value="crescimento">Em crescimento</option>
              </select>
            </label>
          </div>
          <div className={publicStyles.resultsHeading}>
            <div>
              <strong>
                {count}{" "}
                {count === 1 ? "criador encontrado" : "criadores encontrados"}
              </strong>
              {filters.query && <span> para “{filters.query}”</span>}
            </div>
            <button onClick={() => setParams({})}>Limpar filtros</button>
          </div>
          {resultQuery.isLoading ? (
            <SkeletonGrid count={6} />
          ) : resultQuery.isError ? (
            <ErrorState onRetry={() => void resultQuery.refetch()} />
          ) : resultQuery.data?.items.length ? (
            <Reveal className={publicStyles.creatorGrid}>
              {resultQuery.data.items.map((creator) => (
                <CreatorCard
                  creator={creator}
                  key={creator.id}
                  searchEventId={searchEventId}
                  onOpen={(selected) => {
                    const identity = getTrackingIdentity();
                    if (identity && searchEventId)
                      void api
                        .trackSearchResultClick(
                          searchEventId,
                          selected.id,
                          identity,
                        )
                        .catch(reportTrackingFailure);
                  }}
                />
              ))}
            </Reveal>
          ) : (
            <EmptyState
              title="Nenhum match por aqui"
              message="Tente remover um filtro ou buscar por um termo mais amplo."
              action={
                <Button variant="secondary" onClick={() => setParams({})}>
                  Limpar busca
                </Button>
              }
            />
          )}
        </div>
      </section>
    </div>
  );
}

export function RankingPage() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const knownScopes = [
    "geral",
    "crescimento",
    "novos",
    "acessados",
    "compartilhados",
    "semanal",
    "mensal",
  ];
  const routeScope = params.scope || searchParams.get("periodo") || "geral";
  const scopeAliases: Record<string, string> = {
    "criadores-em-crescimento": "crescimento",
    "novos-criadores": "novos",
  };
  const period =
    scopeAliases[routeScope] ||
    (knownScopes.includes(routeScope) ? routeScope : "geral");
  const isScopeRoute =
    knownScopes.includes(routeScope) || Boolean(scopeAliases[routeScope]);
  const category =
    searchParams.get("categoria") || (isScopeRoute ? "" : routeScope);
  const state = stateCodeFromPath(params.estado || "");
  const city = params.cidade || "";
  const rankingQuery = useQuery({
    queryKey: queryKeys.ranking(`${period}:${state || ""}:${city}`, category),
    enabled: !params.estado || Boolean(state),
    queryFn: () =>
      withDemoFallback(
        api.rankings(period, category, { state: state || undefined, city }),
        demoCreators,
      ),
  });
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => withDemoFallback(api.categories(), demoCategories),
  });
  const creators = rankingQuery.data || [];
  const podium = creators.slice(0, 3);
  const listed = creators.slice(podium.length);
  return (
    <div className={publicStyles.page}>
      <Seo
        data={{
          ...getSeoForPath(`/ranking${period !== "geral" ? `/${period}` : ""}`),
          canonicalPath: `/ranking${period !== "geral" ? `/${period}` : ""}`,
          noindex: Boolean(params.estado && !state),
        }}
      />
      <section className={publicStyles.rankingHero}>
        <div
          className={classNames(publicStyles.shell, publicStyles.rankingHeroInner)}
        >
          <div>
            <Eyebrow tone="lime">
              <Flame size={14} /> Atualizado diariamente
            </Eyebrow>
            <h1>
              Quem está
              <br />
              <em>fazendo barulho.</em>
            </h1>
            <p>
              Um retrato dos criadores com presença forte, consistente e em
              movimento.
            </p>
            <div className={classNames(publicStyles.pricingTrust, publicStyles.rankingTrust)}>
              <span><CircleCheck /> 100% orgânico</span>
              <span><TrendingUp /> Atualização diária</span>
              <span><ShieldCheck /> Patrocinado fora do cálculo</span>
            </div>
          </div>
          <div className={publicStyles.rankingTrophy}>
            <span>NA LISTA</span>
            <Trophy />
            <strong>{creators.length || "—"}</strong>
          </div>
        </div>
      </section>
      <section className={classNames(publicStyles.section, publicStyles.rankingSection)}>
        <div className={publicStyles.shell}>
          <Reveal className={publicStyles.rankingControls}>
            <div
              className={publicStyles.segmented}
              role="group"
              aria-label="Tipo do ranking"
            >
              {[
                { id: "geral", label: "Geral" },
                { id: "crescimento", label: "Crescimento" },
                { id: "novos", label: "Novos" },
                { id: "acessados", label: "Acessados" },
                { id: "compartilhados", label: "Compartilhados" },
              ].map((item) => (
                <Link
                  key={item.id}
                  className={period === item.id ? "active" : ""}
                  to={item.id === "geral" ? "/ranking" : `/ranking/${item.id}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <select
              value={category}
              onChange={(event) => {
                const next = new URLSearchParams(searchParams);
                if (event.target.value)
                  next.set("categoria", event.target.value);
                else next.delete("categoria");
                setSearchParams(next);
              }}
              aria-label="Filtrar ranking por categoria"
            >
              <option value="">Todas as categorias</option>
              {categoriesQuery.data?.map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.name}
                </option>
              ))}
            </select>
          </Reveal>
          {rankingQuery.isLoading ? (
            <SkeletonGrid count={6} />
          ) : rankingQuery.isError ? (
            <ErrorState onRetry={() => void rankingQuery.refetch()} />
          ) : creators.length === 0 ? (
            <EmptyState
              title="Nenhum criador por aqui ainda"
              message="Assim que houver criadores publicados nesta seleção, o ranking aparece aqui."
            />
          ) : (
            <>
              {podium.length > 0 && (
                <Reveal
                  className={classNames(
                    publicStyles.podium,
                    podium.length === 1 &&
                      "min-[560px]:mx-auto min-[560px]:max-w-[410px] min-[560px]:grid-cols-1",
                    podium.length === 2 &&
                      "min-[560px]:mx-auto min-[560px]:max-w-[820px] min-[560px]:grid-cols-2",
                    podium.length >= 3 && "min-[560px]:grid-cols-3",
                  )}
                >
                  {podium.map((creator, index) => {
                    const place = index + 1;
                    return (
                      <Link
                        to={`/criador/${creator.slug}?source=internal`}
                        key={creator.id}
                        className={classNames(publicStyles.podiumCard, place === 1 && publicStyles.podiumFirst, place === 2 && publicStyles.podiumSecond)}
                      >
                        <span className={publicStyles.podiumPlace}>
                          {place}º
                        </span>
                        <Avatar
                          name={creator.name}
                          src={creator.avatarUrl}
                          size="xl"
                        />
                        <h2>{creator.name}</h2>
                        <p>
                          {creator.category} · {creator.city}
                        </p>
                        <strong>
                          {formatScore(creator.score)}
                          <small> pts</small>
                        </strong>
                      </Link>
                    );
                  })}
                </Reveal>
              )}
              <Reveal className={publicStyles.rankingList} delayMs={70}>
                {listed.map((creator, index) => (
                  <Link
                    key={creator.id}
                    to={`/criador/${creator.slug}?source=internal`}
                    className={publicStyles.rankingRow}
                  >
                    <span className={publicStyles.rankNumber}>
                      {String(index + podium.length + 1).padStart(2, "0")}
                    </span>
                    <Avatar name={creator.name} src={creator.avatarUrl} />
                    <div className={publicStyles.rankingName}>
                      <strong>{creator.name}</strong>
                      <span>
                        {creator.category} · {creator.city}, {creator.state}
                      </span>
                    </div>
                    <span className={publicStyles.rankingChange}>
                      <TrendingUp size={14} /> {creator.scoreChange || 0}
                    </span>
                    <strong className={publicStyles.rankingScore}>
                      {formatScore(creator.score)}
                    </strong>
                    <ChevronRight />
                  </Link>
                ))}
              </Reveal>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export function CategoriesPage() {
  const [showAllCategories, setShowAllCategories] = useState(false);
  const query = useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => withDemoFallback(api.categories(), demoCategories),
  });
  return (
    <div className={publicStyles.page}>
      <PageSeo path="/categorias" />
      <section className={publicStyles.pageHero}>
        <div className={publicStyles.shell}>
          <Breadcrumbs
            items={[{ label: "Início", to: "/" }, { label: "Categorias" }]}
          />
          <Eyebrow>Explore por universo</Eyebrow>
          <h1>
            Todo talento tem
            <br />
            <em>um território.</em>
          </h1>
          <p>Conheça os universos criativos que movimentam o Score Creator.</p>
          <div className={publicStyles.categoryHeroMetric}>
            <span><Compass size={16} /></span>
            <div>
              <strong>{formatNumber(query.data?.length || 25)} categorias</strong>
              <br /> para encontrar seu próximo match criativo
            </div>
          </div>
        </div>
      </section>
      <section className={classNames(publicStyles.section, publicStyles.categorySection)}>
        <div className={publicStyles.shell}>
          {query.isLoading ? (
            <LoadingState />
          ) : query.isError ? (
            <ErrorState onRetry={() => void query.refetch()} />
          ) : (
            <Reveal className={publicStyles.categoryCollection} threshold={0.02}>
              <div className="relative">
                <CategoryGrid
                  categories={query.data || []}
                  collapseAfterMobile={6}
                  expanded={showAllCategories}
                  id="categories-grid"
                />
                {!showAllCategories && query.data && query.data.length > 6 && (
                  <div className={publicStyles.categoryFade} />
                )}
              </div>
              {query.data && query.data.length > 6 && (
                <button
                  type="button"
                  className={publicStyles.categoryMore}
                  onClick={() => setShowAllCategories((current) => !current)}
                  aria-controls="categories-grid"
                  aria-expanded={showAllCategories}
                >
                  {showAllCategories ? "Mostrar menos" : "Ver todas as categorias"}
                  <ChevronDown
                    size={17}
                    className={classNames(
                      "transition-transform",
                      showAllCategories && "rotate-180",
                    )}
                  />
                </button>
              )}
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}

export function CategoryPage() {
  const { slug = "" } = useParams();
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => withDemoFallback(api.categories(), demoCategories),
  });
  const demoCategoryCreators = demoCreators.filter(
    (creator) => creator.categorySlug === slug,
  );
  const resultQuery = useQuery({
    queryKey: queryKeys.search({ category: slug }),
    queryFn: () =>
      withDemoFallback(api.search({ category: slug }), {
        items: demoCategoryCreators,
        meta: {
          page: 1,
          pageSize: 12,
          total: demoCategoryCreators.length,
          totalPages: 1,
        },
      }),
  });
  const category = categoriesQuery.data?.find((item) => item.slug === slug);
  const name =
    category?.name ||
    slug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  return (
    <div className={publicStyles.page}>
      <PageSeo path={`/categoria/${slug}`} />
      <section className={publicStyles.pageHero}>
        <div className={publicStyles.shell}>
          <Breadcrumbs
            items={[
              { label: "Início", to: "/" },
              { label: "Categorias", to: "/categorias" },
              { label: name },
            ]}
          />
          <Eyebrow>Categoria</Eyebrow>
          <h1>
            Criadores de
            <br />
            <em>{name}.</em>
          </h1>
          <p>
            {category?.description ||
              `Descubra vozes, estilos e projetos que estão renovando ${name.toLowerCase()} no Brasil.`}
          </p>
          <div className={publicStyles.categoryHeroMetric}>
            <span><Compass size={16} /></span>
            <div>
              <strong>{formatNumber(resultQuery.data?.meta.total || 0)} perfis</strong>
              <br /> organizados por relevância orgânica
            </div>
          </div>
        </div>
      </section>
      <section className={classNames(publicStyles.section, publicStyles.categorySection)}>
        <div className={publicStyles.shell}>
          <div className={publicStyles.resultsHeading}>
            <strong>
              {resultQuery.data?.meta.total || 0} criadores nessa categoria
            </strong>
            <Link to="/ranking">
              Ver ranking <ArrowRight size={15} />
            </Link>
          </div>
          {resultQuery.isLoading ? (
            <SkeletonGrid count={6} />
          ) : resultQuery.isError ? (
            <ErrorState onRetry={() => void resultQuery.refetch()} />
          ) : (
            <div className={publicStyles.creatorGrid}>
              {resultQuery.data?.items.map((creator) => (
                <CreatorCard key={creator.id} creator={creator} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function LocalCreatorsPage() {
  const cta = useProfileCta();
  const { categoria = "", estado = "", cidade = "" } = useParams();
  const state = stateCodeFromPath(estado);
  const invalidState = Boolean(estado && !state);
  const filters = useMemo(
    () => ({ category: categoria, state: state || undefined, city: cidade }),
    [categoria, cidade, state],
  );
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => withDemoFallback(api.categories(), demoCategories),
  });
  const demoLocalCreators = demoCreators.filter(
    (creator) =>
      creator.categorySlug === categoria && (!state || creator.state === state),
  );
  const resultQuery = useQuery({
    queryKey: queryKeys.search(filters),
    enabled: !invalidState,
    queryFn: () =>
      withDemoFallback(api.search(filters), {
        items: demoLocalCreators,
        meta: {
          page: 1,
          pageSize: 12,
          total: demoLocalCreators.length,
          totalPages: 1,
        },
      }),
  });
  const category = categoriesQuery.data?.find(
    (item) => item.slug === categoria,
  );
  const categoryName = category?.name || humanizeSlug(categoria || "criadores");
  const cityName = cidade ? humanizeSlug(cidade) : "";
  const stateName = stateNameFromPath(estado) || "Estado inválido";
  const location = cityName ? `${cityName}, ${state || ""}` : stateName;
  const total = resultQuery.data?.meta.total || 0;
  const canonicalPath = `/criadores/${categoria}${estado ? `/${estado.toLowerCase()}` : ""}${cidade ? `/${cidade}` : ""}`;
  if (invalidState)
    return (
      <div className={classNames(publicStyles.shell, publicStyles.profileLoading)}>
        <Seo
          data={{
            title: "Localização não encontrada | Score Creator",
            description: "A localização informada não é válida.",
            canonicalPath,
            noindex: true,
          }}
        />
        <ErrorState
          title="Localização não encontrada"
          message="Use um estado brasileiro válido para explorar criadores locais."
        />
      </div>
    );
  return (
    <div className={publicStyles.page}>
      <Seo
        data={{
          title: `${categoryName} em ${location} | Score Creator`,
          description: `Encontre criadores de ${categoryName} em ${location}.`,
          canonicalPath,
          noindex: total < 3,
        }}
      />
      <section className={publicStyles.pageHero}>
        <div className={publicStyles.shell}>
          <Breadcrumbs
            items={[
              { label: "Início", to: "/" },
              { label: "Categorias", to: "/categorias" },
              { label: categoryName, to: `/categoria/${categoria}` },
              { label: location },
            ]}
          />
          <Eyebrow>Descoberta local</Eyebrow>
          <h1>
            {categoryName}
            <br />
            <em>em {location}.</em>
          </h1>
          <p>
            Perfis públicos encontrados por categoria e localização, ordenados
            por relevância orgânica.
          </p>
          <div className={publicStyles.categoryHeroMetric}>
            <span><MapPin size={16} /></span>
            <div>
              <strong>{formatNumber(total)} perfis em {location}</strong>
              <br /> descoberta local com contexto
            </div>
          </div>
        </div>
      </section>
      <section className={classNames(publicStyles.section, publicStyles.categorySection)}>
        <div className={publicStyles.shell}>
          <div className={publicStyles.resultsHeading}>
            <strong>
              {total}{" "}
              {total === 1 ? "criador encontrado" : "criadores encontrados"}
            </strong>
            <Link to={`/categoria/${categoria}`}>
              Ver categoria nacional <ArrowRight size={15} />
            </Link>
          </div>
          {resultQuery.isLoading ? (
            <SkeletonGrid count={6} />
          ) : resultQuery.isError ? (
            <ErrorState onRetry={() => void resultQuery.refetch()} />
          ) : resultQuery.data?.items.length ? (
            <div className={publicStyles.creatorGrid}>
              {resultQuery.data.items.map((creator) => (
                <CreatorCard key={creator.id} creator={creator} />
              ))}
            </div>
          ) : (
            <EmptyState
              title="Ainda não há perfis suficientes aqui"
              message="Esta página permanece fora dos mecanismos de busca até ganhar conteúdo local relevante."
              action={
                <Link to={cta.freeHref} className={buttonClass("secondary")}>
                  {cta.hasProfile ? "Ver meu perfil" : "Criar perfil nesta região"}
                </Link>
              }
            />
          )}
        </div>
      </section>
    </div>
  );
}

function humanizeSlug(value: string) {
  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function CreatorProfilePage() {
  const cta = useProfileCta();
  const { slug = "" } = useParams();
  const [profileParams] = useSearchParams();
  const [shareStatus, setShareStatus] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("SPAM");
  const [reportDetails, setReportDetails] = useState("");
  const creatorQuery = useQuery({
    queryKey: queryKeys.creator(slug),
    queryFn: () => withDemoFallback(api.creator(slug), demoProfile(slug)),
  });
  const reportMutation = useMutation({
    mutationFn: () =>
      api.reportProfile(creator!.id, reportReason, reportDetails),
    onSuccess: () => window.setTimeout(() => setReportOpen(false), 1400),
  });
  const creator = creatorQuery.data;
  const featuredCampaignId = profileParams.get("featured");
  const validFeaturedCampaignId =
    featuredCampaignId &&
    /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(featuredCampaignId)
      ? featuredCampaignId
      : null;
  const searchEventId = profileParams.get("search");
  const validSearchEventId =
    searchEventId && /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(searchEventId)
      ? searchEventId
      : null;
  const internal = profileParams.get("source") === "internal";
  const attribution = {
    featuredCampaignId: validFeaturedCampaignId,
    searchEventId: validFeaturedCampaignId ? null : validSearchEventId,
    internal: !validFeaturedCampaignId && !validSearchEventId && internal,
  };
  const shareMutation = useMutation({
    mutationFn: ({
      platform,
      identity,
    }: {
      platform: string;
      identity: NonNullable<ReturnType<typeof getTrackingIdentity>>;
    }) => api.trackShare(creator!.id, platform, identity, attribution),
  });

  useEffect(() => {
    function trackView() {
      const identity = getTrackingIdentity();
      if (!identity || !slug) return;
      const request = validFeaturedCampaignId
        ? api.trackAttributedProfileView(
            slug,
            validFeaturedCampaignId,
            identity,
            document.referrer || null,
          )
        : validSearchEventId
          ? api.trackSearchProfileView(
              slug,
              validSearchEventId,
              identity,
              document.referrer || null,
            )
          : internal
            ? api.trackInternalProfileView(
                slug,
                identity,
                document.referrer || null,
              )
            : api.trackProfileView(slug, identity, document.referrer || null);
      void request.catch(reportTrackingFailure);
    }
    trackView();
    window.addEventListener("score-consent-changed", trackView);
    return () => window.removeEventListener("score-consent-changed", trackView);
  }, [internal, slug, validFeaturedCampaignId, validSearchEventId]);

  async function share() {
    if (!creator) return;
    const url = `${window.location.origin}/criador/${creator.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${creator.name} no Score Creator`,
          text: creator.headline,
          url,
        });
        const identity = getTrackingIdentity();
        if (identity)
          void shareMutation.mutateAsync({ platform: "NATIVE", identity });
        setShareStatus("Perfil compartilhado");
      } else {
        await copyText(url);
        const identity = getTrackingIdentity();
        if (identity)
          void shareMutation.mutateAsync({ platform: "COPY_LINK", identity });
        setShareStatus("Link copiado");
      }
    } catch (error) {
      if ((error as Error).name !== "AbortError")
        setShareStatus("Não foi possível compartilhar");
    }
  }

  if (creatorQuery.isLoading)
    return (
      <div className={publicStyles.profileLoading}>
        <LoadingState label="Abrindo o perfil" />
      </div>
    );
  if (creatorQuery.isError || !creator)
    return (
      <div className={classNames(publicStyles.shell, publicStyles.profileLoading)}>
        <ErrorState
          title="Perfil não encontrado"
          message="Este perfil pode ter mudado de endereço ou não está mais disponível."
        />
      </div>
    );
  return (
    <div
      className="public-profile @container min-h-[70vh] bg-[linear-gradient(180deg,#f0ebf2_0%,#e9e3ed_100%)]"
      style={publicProfileTheme(creator.themeJson?.preset)}
    >
      <Seo
        data={{
          ...getSeoForPath(`/criador/${creator.slug}`),
          title: `${creator.name} | Score Creator`,
          description: creator.headline,
          canonicalPath: `/criador/${creator.slug}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Person",
            name: creator.name,
            description: creator.bio,
            url: `/criador/${creator.slug}`,
            homeLocation: {
              "@type": "Place",
              name: `${creator.city}, ${creator.state}`,
            },
          },
        }}
      />
      <PublicProfileView
        creator={creator}
        onShare={() => void share()}
        shareStatus={shareStatus}
        onReport={() => setReportOpen(true)}
        onLinkClick={(link) => {
          const identity = getTrackingIdentity();
          if (!identity) return;
          const linkId = /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(link.id)
            ? link.id
            : null;
          void api
            .trackLinkClick(
              creator.id,
              linkId,
              link.platform,
              identity,
              attribution,
            )
            .catch(reportTrackingFailure);
        }}
      />
      {Boolean(creator.related?.length) && (
        <section
          className={classNames(publicStyles.section, publicStyles.sectionCream)}
        >
          <div className={publicStyles.shell}>
            <div
              className={classNames(publicStyles.sectionHeading, publicStyles.sectionHeadingSplit)}
            >
              <div>
                <Eyebrow>Continue explorando</Eyebrow>
                <h2>
                  Mais vozes de
                  <br />
                  <em>{creator.category}.</em>
                </h2>
              </div>
              <Link
                to={`/categoria/${creator.categorySlug}`}
                className={buttonClass("secondary")}
              >
                Ver categoria <ArrowRight size={17} />
              </Link>
            </div>
            <div className={publicStyles.creatorGrid}>
              {creator.related?.map((related) => (
                <CreatorCard key={related.id} creator={related} />
              ))}
            </div>
          </div>
        </section>
      )}
      <section className={publicStyles.profileBottomCta}>
        <div
          className={classNames(
            publicStyles.shell,
            publicStyles.profileBottomCtaInner,
          )}
        >
          <div>
            <strong>{cta.hasProfile ? "Seu perfil também está aqui." : "Também cria conteúdo?"}</strong>
            <span>
              {cta.hasProfile
                ? "Acompanhe seu Creator Score no painel."
                : "Monte seu perfil e apareça por aqui."}
            </span>
          </div>
          <Link to={cta.freeHref} className={buttonClass("primary")}>
            {!cta.signedIn
              ? "Criar meu perfil"
              : cta.hasProfile
                ? "Ir para meu painel"
                : "Concluir meu perfil"}{" "}
            <ArrowRight size={17} />
          </Link>
        </div>
      </section>
      {reportOpen && (
        <div
          className={publicStyles.modalBackdrop}
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setReportOpen(false);
          }}
        >
          <div
            className={publicStyles.reportDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-title"
          >
            <button
              className={publicStyles.dialogClose}
              onClick={() => setReportOpen(false)}
              aria-label="Fechar denúncia"
            >
              ×
            </button>
            <Eyebrow>Ajude a comunidade</Eyebrow>
            <h2 id="report-title">Denunciar {creator.name}</h2>
            <p>
              Nossa equipe analisa cada relato. O criador não recebe seus dados.
            </p>
            {reportMutation.isSuccess ? (
              <div className={successMessageClass}>
                <CircleCheck /> Denúncia recebida. Obrigado.
              </div>
            ) : (
              <>
                <label className={formFieldClass}>
                  <span>Motivo</span>
                  <select
                    value={reportReason}
                    onChange={(event) => setReportReason(event.target.value)}
                  >
                    <option value="SPAM">Spam</option>
                    <option value="IMPERSONATION">
                      Fingindo ser outra pessoa
                    </option>
                    <option value="INAPPROPRIATE_CONTENT">
                      Conteúdo inadequado
                    </option>
                    <option value="FRAUD">Possível fraude</option>
                    <option value="COPYRIGHT">Direitos autorais</option>
                    <option value="OTHER">Outro</option>
                  </select>
                </label>
                <label className={formFieldClass}>
                  <span>Detalhes (opcional)</span>
                  <textarea
                    rows={4}
                    value={reportDetails}
                    onChange={(event) => setReportDetails(event.target.value)}
                    placeholder="Conte o que você encontrou…"
                  />
                </label>
                {reportMutation.isError && (
                  <div className={formAlertClass}>
                    {(reportMutation.error as Error).message}
                  </div>
                )}
                <div className={publicStyles.dialogActions}>
                  <Button variant="ghost" onClick={() => setReportOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => reportMutation.mutate()}
                    loading={reportMutation.isPending}
                  >
                    Enviar denúncia
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PricingPage() {
  const pricingQuery = useQuery({
    queryKey: queryKeys.pricing,
    queryFn: () =>
      withDemoFallback(api.pricing(), {
        premium: {
          id: "demo",
          name: "Premium mensal",
          priceInCents: 2790,
          currency: "BRL",
          billingPeriod: "MONTHLY" as const,
          trialDays: 0,
        },
        premiumPlans: [
          {
            id: "demo",
            name: "Premium mensal",
            priceInCents: 2790,
            currency: "BRL",
            billingPeriod: "MONTHLY" as const,
            monthlyEquivalentInCents: 2790,
            savingsPerYearInCents: 0,
            savingsPercent: 0,
            trialDays: 0,
          },
        ],
        featured: [],
        notice: "A assinatura Premium e o destaque são independentes.",
      }),
  });
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("MONTHLY");
  const featuresFree = [
    "Perfil público",
    "Até 3 links",
    "Creator Score atual",
    "Aparecer na busca",
  ];
  const featuresPremium = [
    "Links ilimitados",
    "Analytics detalhado",
    "Histórico do Creator Score",
    "Personalização do perfil",
    "Selo Premium",
    "Dados preservados após downgrade",
  ];
  const premium = pricingQuery.data?.premium;
  // An API deployed before the annual plan sends only `premium`; deriving the
  // monthly entry from it keeps the page rendering instead of blanking out.
  const plans: PremiumPlan[] =
    pricingQuery.data?.premiumPlans ||
    (premium
      ? [{ ...premium, monthlyEquivalentInCents: premium.priceInCents, savingsPerYearInCents: 0, savingsPercent: 0 }]
      : []);
  const annualPlan = plans.find((plan) => plan.billingPeriod === "YEARLY");
  const selectedPlan =
    plans.find((plan) => plan.billingPeriod === billingPeriod) || plans[0];
  const featured =
    pricingQuery.data?.featured.filter((item) => item.isActive) || [];
  const { signedIn, hasProfile, isPremium, freeHref, premiumHref, featuredHref } = useProfileCta();
  const premiumCheckoutHref =
    selectedPlan?.billingPeriod === "YEARLY"
      ? `${premiumHref}${premiumHref.includes("?") ? "&" : "?"}ciclo=anual`
      : premiumHref;
  const premiumCta = isPremium ? "Gerenciar assinatura" : "Assinar Premium";
  const freeCta = !signedIn ? "Criar perfil" : hasProfile ? "Ver meu perfil" : "Completar meu perfil";
  const featuredCta = !signedIn
    ? "Criar perfil para contratar"
    : hasProfile
      ? "Contratar destaque"
      : "Completar perfil para contratar";
  return (
    <div className={publicStyles.page}>
      <PageSeo path="/precos" />
      <section className={publicStyles.pricingHero}>
        <div className={publicStyles.shell}>
          <Eyebrow tone="lime">Simples, sem pegadinha</Eyebrow>
          <h1>
            Seu trabalho merece
            <br />
            <em>uma presença à altura.</em>
          </h1>
          <p>
            Publique de graça e assine o Premium quando fizer sentido. Sem
            fidelidade, cancele quando quiser.
          </p>
          <div className={publicStyles.pricingTrust}>
            <span><CircleCheck /> Perfil gratuito para sempre</span>
            <span><ShieldCheck /> Cancele quando quiser</span>
            <span><Sparkles /> Sem fidelidade</span>
          </div>
          {annualPlan && (
            <div
              className={publicStyles.billingToggle}
              role="radiogroup"
              aria-label="Ciclo de cobrança do Premium"
            >
              {plans.map((plan) => {
                const active = plan.billingPeriod === billingPeriod;
                return (
                  <button
                    key={plan.billingPeriod}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setBillingPeriod(plan.billingPeriod)}
                    className={classNames(
                      publicStyles.billingOption,
                      active && publicStyles.billingOptionActive,
                    )}
                  >
                    {plan.billingPeriod === "YEARLY" ? "Anual" : "Mensal"}
                    {/*
                      The pill carries the percentage and the card underneath
                      carries the amount, so the reader gets both without either
                      one crowding the control.
                    */}
                    {plan.savingsPercent > 0 && (
                      <span
                        className={classNames(
                          publicStyles.billingSaveTag,
                          active && publicStyles.billingSaveTagActive,
                        )}
                      >
                        −{plan.savingsPercent}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <section
        className={publicStyles.pricingSection}
      >
        <div className={publicStyles.shell}>
          {pricingQuery.isLoading ? (
            <LoadingState label="Carregando os planos" />
          ) : pricingQuery.isError || !premium ? (
            <ErrorState
              message="Não foi possível consultar o preço atual."
              onRetry={() => void pricingQuery.refetch()}
            />
          ) : (
            <>
              <div className={publicStyles.pricingGrid}>
                <Card className={classNames(publicStyles.priceCard, "order-2 min-[700px]:order-1")}>
                  {signedIn && !isPremium && (
                    <span className={publicStyles.currentPlanTag}>
                      <CircleCheck /> SEU PLANO
                    </span>
                  )}
                  <span className={`plan-label ${publicStyles.planLabel}`}>
                    Gratuito
                  </span>
                  <div className={`price ${publicStyles.price}`}>
                    <strong>R$ 0</strong>
                    <span>para sempre</span>
                  </div>
                  <p>
                    Para colocar seu nome no mapa e começar a ser encontrado.
                  </p>
                  <Link
                    to={freeHref}
                    className={buttonClass("secondary", "lg")}
                  >
                    {freeCta}
                  </Link>
                  <ul>
                    {featuresFree.map((feature) => (
                      <li key={feature}>
                        <Check />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card>
                <Card
                  className={classNames(publicStyles.priceCard, publicStyles.pricePremium, "order-1 min-[700px]:order-2")}
                >
                  {isPremium ? (
                    <span className={publicStyles.currentPlanTag}>
                      <Crown /> SEU PLANO
                    </span>
                  ) : (
                    <span className={publicStyles.popularTag}>
                      <Sparkles /> PREMIUM
                    </span>
                  )}
                  <span className={`plan-label ${publicStyles.planLabel}`}>
                    {selectedPlan?.name || premium.name}
                  </span>
                  {/*
                    Both plans show what they cost per month, so the comparison
                    is one number against another. The line underneath says what
                    actually leaves the card, because the annual plan charges
                    twelve months at once and burying that would be a surprise
                    at checkout.
                  */}
                  <div className={`price ${publicStyles.price}`}>
                    <strong>
                      {formatCurrency(
                        selectedPlan?.monthlyEquivalentInCents ?? premium.priceInCents,
                        selectedPlan?.currency || premium.currency,
                      )}
                    </strong>
                    <span>por mês</span>
                  </div>
                  <p className={publicStyles.priceCadence}>
                    {selectedPlan?.billingPeriod === "YEARLY"
                      ? `${formatCurrency(selectedPlan.priceInCents, selectedPlan.currency)} cobrados uma vez por ano — você economiza ${formatCurrency(selectedPlan.savingsPerYearInCents, selectedPlan.currency)}.`
                      : annualPlan
                        ? "Cobrado todo mês. Troque para o anual quando quiser."
                        : "Cobrado todo mês. Cancele quando quiser."}
                  </p>
                  <p>
                    Para entender sua presença e transformar atenção em
                    oportunidade.
                  </p>
                  <Link to={premiumCheckoutHref} className={buttonClass("primary", "lg")}>
                    {premiumCta} <ArrowRight />
                  </Link>
                  <ul>
                    {featuresPremium.map((feature) => (
                      <li key={feature}>
                        <Check />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <small>
                    {isPremium
                      ? "Seu acesso Premium já está ativo. Gerencie a cobrança e a renovação no painel."
                      : "A cobrança começa na confirmação do pagamento. Cancele quando quiser."}
                  </small>
                </Card>
              </div>
              <div
                className={classNames(publicStyles.sectionHeading, publicStyles.pricingFeatured)}
              >
                <Eyebrow>Destaque avulso</Eyebrow>
                <h2>
                  Visibilidade por campanha,
                  <br />
                  <em>sem alterar seu score.</em>
                </h2>
                <p>
                  Escolha um período. A contratação é separada da assinatura
                  Premium.
                </p>
              </div>
              {featured.length ? (
                <div className={publicStyles.featuredPlans}>
                  {featured.map((item, index) => (
                    <Card
                      key={item.id}
                      className={index === 1 ? publicStyles.featuredPlanPopular : ""}
                    >
                      {index === 1 && (
                        <span className={publicStyles.popularTag}>
                          <Sparkles /> MAIS ESCOLHIDO
                        </span>
                      )}
                      <span className={publicStyles.featuredDays}>
                        {item.days} dias
                      </span>
                      <h2>{item.name}</h2>
                      <p>
                        Campanha avulsa de visibilidade com métricas
                        identificadas como tráfego patrocinado.
                      </p>
                      <strong>
                        {formatCurrency(item.priceInCents, item.currency)}
                      </strong>
                      <Link
                        to={featuredHref}
                        className={buttonClass(
                          index === 1 ? "primary" : "secondary",
                        )}
                      >
                        {featuredCta}
                      </Link>
                    </Card>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="Pacotes temporariamente indisponíveis"
                  message="Novas opções de destaque serão publicadas aqui."
                />
              )}
              <div className={publicStyles.featuredDisclaimer}>
                <Info />
                <p>
                  <strong>
                    {pricingQuery.data?.notice ||
                      "Destaque e assinatura são independentes."}
                  </strong>{" "}
                  Campanhas não aumentam artificialmente o Creator Score nem a
                  posição no ranking orgânico.
                </p>
              </div>
            </>
          )}
        </div>
      </section>
      <section className={publicStyles.pricingGuaranteeSection}>
        <div className={classNames(publicStyles.shell, publicStyles.guarantee)}>
          <ShieldCheck />
          <div>
            <h2>Você continua no controle.</h2>
            <p>
              O perfil público é gratuito para sempre e o Premium não tem
              fidelidade. Ao cancelar, seu Premium segue ativo até o fim do
              período já pago.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const institutionalContent: Record<
  string,
  {
    eyebrow: string;
    title: string;
    intro: string;
    sections: Array<{ title: string; text: string }>;
  }
> = {
  sobre: {
    eyebrow: "Por trás do score",
    title: "Visibilidade com mais clareza.",
    intro:
      "O Score Creator nasceu para aproximar talento e oportunidade sem reduzir criação a uma contagem de seguidores.",
    sections: [
      {
        title: "Nossa ideia",
        text: "Criadores precisam de um espaço que organize sua presença e ajude outras pessoas a descobrir o que fazem de melhor.",
      },
      {
        title: "Como olhamos o score",
        text: "Completude, engajamento, descoberta e atividade compõem uma leitura transparente. O número aponta caminhos; nunca define o valor de alguém. Logo abaixo você encontra o detalhe de cada dimensão.",
      },
      {
        title: "Feito no Brasil",
        text: "Categorias, cidades e contextos locais importam. Queremos uma plataforma tão diversa quanto quem cria por aqui.",
      },
    ],
  },
  termos: {
    eyebrow: "Última atualização: agosto de 2026",
    title: "Termos de uso.",
    intro:
      "Estes termos explicam as regras para usar o Score Creator de forma segura e responsável.",
    sections: [
      {
        title: "Sua conta",
        text: "Você é responsável por manter seus dados corretos, proteger seu acesso e usar a plataforma de acordo com a lei.",
      },
      {
        title: "Conteúdo e perfis",
        text: "Você mantém os direitos sobre o que publica e nos autoriza a exibir esse conteúdo para operar seu perfil e a descoberta pública.",
      },
      {
        title: "Assinaturas",
        text: "A assinatura Premium é mensal e renova automaticamente, salvo cancelamento anterior.",
      },
    ],
  },
  privacidade: {
    eyebrow: "Privacidade e LGPD",
    title: "Seus dados, com respeito.",
    intro:
      "Explicamos aqui o que coletamos, por que usamos e como você pode exercer seus direitos.",
    sections: [
      {
        title: "Dados que usamos",
        text: "Tratamos dados de conta, perfil, assinatura, consentimento e métricas necessárias para entregar os recursos contratados.",
      },
      {
        title: "Métricas e retenção",
        text: "Eventos detalhados são mantidos por até 180 dias e agregados por até 24 meses. Identificadores de IP são transformados antes do armazenamento.",
      },
      {
        title: "Seus direitos",
        text: "Você pode pedir acesso, correção, portabilidade ou exclusão dos seus dados pelos canais de contato da plataforma.",
      },
    ],
  },
  diretrizes: {
    eyebrow: "Comunidade",
    title: "Diretrizes de convivência.",
    intro:
      "Queremos um espaço onde trabalho autoral, respeito e confiança caminhem juntos.",
    sections: [
      {
        title: "Seja verdadeiro",
        text: "Publique informações, links e trabalhos que representem você e para os quais tenha autorização.",
      },
      {
        title: "Respeite pessoas",
        text: "Não aceitamos assédio, discriminação, fraude ou conteúdo que coloque outras pessoas em risco.",
      },
      {
        title: "Ajude a comunidade",
        text: "Encontrou algo inadequado? Use a denúncia no perfil para que nossa equipe possa analisar.",
      },
    ],
  },
  contato: {
    eyebrow: "Fale com a gente",
    title: "Estamos por perto.",
    intro: "Dúvidas, ideias ou algum problema? Nossa equipe quer ouvir você.",
    sections: [
      {
        title: "Suporte",
        text: "Para ajuda com sua conta, perfil ou assinatura, escreva para suporte@scorecreator.com.br.",
      },
      {
        title: "Parcerias",
        text: "Marcas, agências e comunidades podem falar com parcerias@scorecreator.com.br.",
      },
    ],
  },
  "como-funciona": {
    eyebrow: "Do rascunho à descoberta",
    title: "Como o Score Creator funciona.",
    intro:
      "Você organiza sua presença, publica seu perfil e acompanha sinais claros para evoluir.",
    sections: [
      {
        title: "Crie seu perfil",
        text: "Reúna apresentação, categoria, localização e canais em um endereço público fácil de compartilhar.",
      },
      {
        title: "Publique gratuitamente",
        text: "A publicação do perfil não exige cartão nem Checkout. O Premium é opcional e pode ser ativado depois no painel.",
      },
      {
        title: "Entenda seu score",
        text: "Completude, engajamento, descoberta e atividade formam uma leitura versionada da sua presença, sem misturar tráfego patrocinado ao ranking orgânico.",
      },
    ],
  },
  faq: {
    eyebrow: "Perguntas frequentes",
    title: "Respostas sem rodeios.",
    intro:
      "Os pontos principais sobre perfil, assinatura, indicação e Creator Score.",
    sections: [
      {
        title: "Preciso pagar para ter perfil?",
        text: "Não. Você pode criar, publicar e compartilhar seu perfil gratuitamente, sem cadastrar cartão.",
      },
      {
        title: "Quando o Premium é liberado?",
        text: "Assim que o webhook da Stripe confirma o pagamento do Checkout. A cobrança é mensal e não há fidelidade.",
      },
      {
        title: "Como funciona o bônus?",
        text: "Dez contas novas e verificadas pelo seu link liberam uma única recompensa de 30 dias Premium, aplicada conforme a situação da sua assinatura.",
      },
      {
        title: "Destaque aumenta o score?",
        text: "Não. O tráfego patrocinado aparece no Analytics, mas é excluído do Creator Score e dos rankings orgânicos.",
      },
    ],
  },
};

/*
 * Commercial explanation of the Creator Score. It says what each dimension
 * rewards and how the scale behaves, on purpose without the thresholds that
 * max a metric: published, those numbers turn the score into a checklist to
 * farm instead of a reading of real presence.
 */
const SCORE_DIMENSIONS = [
  {
    icon: CircleCheck,
    name: "Perfil completo",
    weight: "30%",
    summary:
      "O ponto de partida. Um perfil que se explica sozinho é encontrado com mais facilidade e converte melhor quem chega.",
    items: ["Foto, bio e apresentação", "Categoria e localização", "Instagram e outras redes"],
  },
  {
    icon: BarChart3,
    name: "Engajamento",
    weight: "35%",
    summary:
      "O que acontece depois que alguém chega ao seu perfil: visitas reais, cliques nos seus links e compartilhamentos.",
    items: ["Visitantes únicos no período", "Cliques nos seus links", "Compartilhamentos do perfil", "Proporção entre visitas e cliques"],
  },
  {
    icon: Compass,
    name: "Descoberta",
    weight: "25%",
    summary:
      "O quanto você aparece para quem ainda não te conhece — e se essa aparição vira visita.",
    items: ["Aparições nas buscas da plataforma", "Cliques a partir da busca", "Variedade de termos que levam até você", "Evolução em relação ao período anterior"],
  },
  {
    icon: Zap,
    name: "Atividade",
    weight: "10%",
    summary:
      "Sinais de que o perfil está vivo. Vale pouco, mas é a parte que depende só de você e não do público.",
    items: ["Perfil atualizado recentemente", "Links funcionando e seguros", "Uso do painel"],
  },
];

const SCORE_PRINCIPLES = [
  {
    icon: ShieldCheck,
    title: "É a sua nota, não uma comparação",
    text: "Seu score não muda porque outro criador entrou, saiu ou cresceu. Ele mede o que você fez, então a evolução ao longo do tempo é comparável de verdade.",
  },
  {
    icon: TrendingUp,
    title: "Os primeiros passos valem mais",
    text: "Sair do zero pesa mais do que dobrar um número já alto. Quem está começando vê o score reagir logo nas primeiras visitas.",
  },
  {
    icon: Sparkles,
    title: "Crescer sempre soma",
    text: "Não existe um patamar em que o esforço para de contar. Cada avanço continua somando, cada um um pouco menos que o anterior.",
  },
];

function ScoreExplainer() {
  return (
    <section className={publicStyles.scoreExplainerSection} id="score">
      <div className={publicStyles.shellNarrow}>
        <Reveal className={publicStyles.scoreExplainerHead}>
          <Eyebrow>Como o Creator Score funciona</Eyebrow>
          <h2>
            Uma leitura da sua presença,
            <br />
            <em>não um placar de seguidores.</em>
          </h2>
          <p>
            O Creator Score reúne quatro dimensões em um número de 0 a 100.
            Cada uma responde a uma pergunta diferente sobre o seu perfil, e
            juntas mostram onde vale investir energia agora.
          </p>
        </Reveal>
        <Reveal className={publicStyles.scoreDimensionGrid} delayMs={60}>
          {SCORE_DIMENSIONS.map(({ icon: Icon, name, weight, summary, items }) => (
            <article className={publicStyles.scoreDimensionCard} key={name}>
              <div className={publicStyles.scoreDimensionHead}>
                <span>
                  <Icon aria-hidden="true" />
                </span>
                <h3>{name}</h3>
                <span className={publicStyles.scoreDimensionWeight}>{weight}</span>
              </div>
              <p>{summary}</p>
              <ul>
                {items.map((item) => (
                  <li key={item}>
                    <Check aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </Reveal>
        <Reveal className={publicStyles.scorePrincipleGrid} delayMs={90}>
          {SCORE_PRINCIPLES.map(({ icon: Icon, title, text }) => (
            <div className={publicStyles.scorePrincipleCard} key={title}>
              <Icon aria-hidden="true" />
              <strong>{title}</strong>
              <p>{text}</p>
            </div>
          ))}
        </Reveal>
        <Reveal className={publicStyles.scoreExcluded} delayMs={120}>
          <ShieldCheck aria-hidden="true" />
          <div>
            <h3>O que nunca entra na conta</h3>
            <ul>
              <li>
                <Check aria-hidden="true" />
                Tráfego de campanhas de destaque. Elas ampliam seu alcance e
                aparecem no Analytics separadas, mas não movem o score nem o
                ranking orgânico.
              </li>
              <li>
                <Check aria-hidden="true" />
                Suas próprias visitas e cliques no seu perfil. Testar seus links
                é útil para você e invisível para a nota.
              </li>
              <li>
                <Check aria-hidden="true" />
                Número de seguidores nas redes. O score olha o que acontece aqui,
                não o tamanho da sua audiência em outro lugar.
              </li>
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function InstitutionalPage({
  type,
}: {
  type: keyof typeof institutionalContent;
}) {
  const content = institutionalContent[type];
  return (
    <div className={publicStyles.page}>
      <PageSeo path={`/${type}`} />
      <section className={publicStyles.institutionalHero}>
        <div className={publicStyles.shellNarrow}>
          <Eyebrow>{content.eyebrow}</Eyebrow>
          <h1>{content.title}</h1>
          <p>{content.intro}</p>
        </div>
      </section>
      <section className={publicStyles.section}>
        <div
          className={classNames(publicStyles.shellNarrow, publicStyles.proseGrid)}
        >
          {content.sections.map((section, index) => (
            <article key={section.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h2>{section.title}</h2>
                <p>{section.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
      {/* The #score anchor now belongs to the detailed explainer, which is what
          every "entenda este score" link across the product points at. */}
      {type === "sobre" && <ScoreExplainer />}
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className={publicStyles.notFound}>
      <PageSeo path="/404" />
      <span>404</span>
      <h1>Essa página saiu para criar.</h1>
      <p>O endereço pode ter mudado ou nunca ter existido.</p>
      <Link to="/" className={buttonClass("dark")}>
        Voltar ao início
      </Link>
    </div>
  );
}

export function LegacyCategoryRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/categoria/${slug}`} replace />;
}
